import { useNavigate, useSearch } from "@tanstack/react-router"
import { useSyncExternalStore, type ReactNode } from "react"

import { segmentClass, segmentGroupClass } from "@/components/layout/nav-link"

const noSubscription = () => () => {}
// The static HTML is always the monthly grid; the URL is read after hydration.
const hydrated = () => true
const onServer = () => false

/**
 * Monthly/yearly toggle for the pricing page. Both grids are rendered on the
 * server and this only picks which one shows, so the page stays static and
 * cacheable instead of rendering per request. `?billing=yearly` (where sign-up
 * returns a visitor who picked yearly) opens on the yearly grid.
 */
export function IntervalSwitch({
  monthly,
  yearly,
  yearlyNote,
}: {
  monthly: ReactNode
  yearly: ReactNode
  yearlyNote: string | null
}) {
  const afterHydration = useSyncExternalStore(
    noSubscription,
    hydrated,
    onServer
  )
  const yearlyInUrl = useSearch({
    from: "/pricing",
    select: (search) => search.billing === "yearly",
  })
  const navigate = useNavigate({ from: "/pricing" })
  const interval = afterHydration && yearlyInUrl ? "year" : "month"

  return (
    <>
      <div className="mb-10 flex justify-center">
        <div
          role="group"
          aria-label="Billing period"
          className={segmentGroupClass}
        >
          {(["month", "year"] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={value === interval}
              onClick={() => {
                // Keep the choice in the URL so a shared link or a reload opens
                // on the same grid. Replace, not push: a toggle isn't a page.
                // Through the router, so the entry keeps its scroll position
                // and a link to plain /pricing opens monthly again.
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    billing: value === "year" ? "yearly" : undefined,
                  }),
                  replace: true,
                  resetScroll: false,
                })
              }}
              className={segmentClass(value === interval)}
            >
              {value === "month" ? "Monthly" : "Yearly"}
              {value === "year" && yearlyNote && (
                <span className="ml-1.5 text-xs text-muted-foreground">
                  {yearlyNote}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      {interval === "month" ? monthly : yearly}
    </>
  )
}
